/**
 * MailrChannel.ts - Mailr email notification channel
 * 
 * CHANGE HISTORY:
 * - 251030_1217 [pending] danielbeer: Implemented actual Mailr API calls
 * - 251030_0907 [pending] danielbeer: Created Mailr email channel using Mailr SMTP infrastructure
 * 
 * Uses Mailr container's SMTP infrastructure for outbound email notifications.
 * Mailr provides: Postfix MTA, DKIM signing, SPF/DMARC validation, anti-spam.
 */

import { Notification } from '../models/Notification';

export interface MailrConfig {
  apiUrl: string;  // Mailr API endpoint
  apiKey?: string; // Optional API key for authentication
  from?: string;   // Default from address
  loggr?: any;
}

/**
 * Mailr email channel - uses Mailr container's SMTP infrastructure
 */
export class MailrChannel {
  private config: MailrConfig;
  private loggr: any;
  
  constructor(config: MailrConfig) {
    this.config = config;
    this.loggr = config.loggr;
  }
  
  /**
   * Send notification through Mailr
   */
  async send(notification: Notification): Promise<any> {
    try {
      if (!this.config.apiUrl) {
        throw new Error('Mailr not configured');
      }
      
      this.loggr?.info('Sending email via Mailr', {
        userId: notification.userId,
        title: notification.title
      });
      
      // Get user's email address
      // TODO: Look up user email from userId via Authr or Contactr
      const toEmail = (notification.data as any)?.email || notification.userId;
      
      // Build email HTML
      const html = this.buildEmailHTML(notification);
      
      // Prepare request payload
      const payload = {
        from: this.config.from || 'notifications@company.com',
        to: toEmail,
        subject: notification.title,
        html: html,
        priority: this.mapPriority(notification.priority || 'normal')
      };
      
      // Prepare headers
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      
      if (this.config.apiKey) {
        headers['Authorization'] = `Bearer ${this.config.apiKey}`;
      }
      
      // Send via Mailr API
      const response = await fetch(`${this.config.apiUrl}/api/send`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Mailr API error: ${response.status} - ${errorText}`);
      }
      
      const result = await response.json();
      const messageId = result.messageId || result.id;
      
      this.loggr?.info('Email sent via Mailr', {
        userId: notification.userId,
        toEmail,
        messageId,
        subject: notification.title
      });
      
      return {
        success: true,
        messageId,
        channel: 'email',
        provider: 'mailr',
        accepted: result.accepted,
        rejected: result.rejected
      };
      
    } catch (error) {
      this.loggr?.error('Failed to send email via Mailr', {
        userId: notification.userId,
        error: error instanceof Error ? error.message : String(error)
      });
      
      throw error;
    }
  }
  
  /**
   * Build HTML email from notification
   */
  private buildEmailHTML(notification: Notification): string {
    const iconEmoji = notification.icon || this.getDefaultIcon(notification.type);
    
    let html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${notification.title}</title>
  <style>
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; line-height: 1.6; color: #333; }
    .container { max-width: 600px; margin: 0 auto; padding: 20px; }
    .header { background: ${this.getTypeColor(notification.type)}; color: white; padding: 20px; border-radius: 8px 8px 0 0; }
    .content { background: #f9f9f9; padding: 20px; border-radius: 0 0 8px 8px; }
    .actions { margin-top: 20px; }
    .button { display: inline-block; padding: 12px 24px; margin: 5px; background: #007bff; color: white; text-decoration: none; border-radius: 4px; }
    .footer { margin-top: 20px; font-size: 12px; color: #666; text-align: center; }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1>${iconEmoji} ${notification.title}</h1>
    </div>
    <div class="content">
      <p>${notification.message}</p>
`;
    
    // Add action buttons if present
    if (notification.actions && notification.actions.length > 0) {
      html += '<div class="actions">';
      notification.actions.forEach((action) => {
        const url = action.url || notification.url || '#';
        html += `<a href="${url}" class="button">${action.label}</a>`;
      });
      html += '</div>';
    }
    
    // Add footer
    html += `
    </div>
    <div class="footer">
      <p>This is an automated notification from OpCentral</p>
    </div>
  </div>
</body>
</html>
`;
    
    return html;
  }
  
  /**
   * Get default icon for notification type
   */
  private getDefaultIcon(type: string): string {
    const icons: Record<string, string> = {
      info: 'ℹ️',
      success: '✅',
      warning: '⚠️',
      error: '❌'
    };
    return icons[type] || 'ℹ️';
  }
  
  /**
   * Get color for notification type
   */
  private getTypeColor(type: string): string {
    const colors: Record<string, string> = {
      info: '#17a2b8',
      success: '#28a745',
      warning: '#ffc107',
      error: '#dc3545'
    };
    return colors[type] || '#17a2b8';
  }
  
  /**
   * Map Notifyr priority to email priority
   */
  private mapPriority(priority: string): number {
    const map: Record<string, number> = {
      low: 5,
      normal: 3,
      high: 2,
      urgent: 1
    };
    return map[priority] || 3;
  }
  
  /**
   * Check if channel is configured
   */
  isConfigured(): boolean {
    return !!this.config.apiUrl;
  }
}
